import React, { useEffect, useState, Suspense, useCallback, memo } from "react";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import ScrollButton from "./components/ScrollButton";
import Preloader from "./components/Preloader";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { ToastProvider } from "./context/ToastContext";
import ModernToastContainer from "./components/ModernToastContainer";
import { useLocation } from "react-router-dom";
import api from "./api";
import { toast } from "./utils/toast";
import "react-toastify/dist/ReactToastify.css";

// Code splitting: Lazy load all route components
const Home = React.lazy(() => import("./pages/Home/Home"));
const Products = React.lazy(() => import("./pages/Products/Products"));
const ProductDetails = React.lazy(() => import("./pages/Products/ProductDetails"));
const ProductCategories = React.lazy(() => import("./pages/Products/ProductCategories"));
const ProductSubCategories = React.lazy(() => import("./pages/Products/ProductSubCategories"));
const ContactUs = React.lazy(() => import("./pages/ContactUs"));
const WhyUs = React.lazy(() => import("./pages/WhyUs"));
const Career = React.lazy(() => import("./pages/Career"));
const Sitemap = React.lazy(() => import("./pages/Sitemap"));
const Faqs = React.lazy(() => import("./pages/Faqs"));
const AboutUs = React.lazy(() => import("./pages/AboutUs"));
const Blogs = React.lazy(() => import("./pages/Blogs/Blogs"));
const BlogDetails = React.lazy(() => import("./pages/Blogs/BlogDetails"));
const Projects = React.lazy(() => import("./pages/Projects/Projects"));
const ProjectDetails = React.lazy(() => import("./pages/Projects/ProjectDetails"));
const Dashboard = React.lazy(() => import("./pages/Dashboard/Dashboard"));
const PurchaseHistory = React.lazy(() => import("./pages/Dashboard/PurchaseHistory"));
const Wishlists = React.lazy(() => import("./pages/Dashboard/Wishlists"));
const Profile = React.lazy(() => import("./pages/Dashboard/Profile"));
const OrderDetails = React.lazy(() => import("./pages/Dashboard/OrderDetails"));
const Notification = React.lazy(() => import("./pages/Dashboard/Notification"));
const WarrantyPolicy = React.lazy(() => import("./pages/Policy/WarrantyPolicy"));
const ShippingPolicy = React.lazy(() => import("./pages/Policy/ShippingPolicy"));
const ReturnPolicy = React.lazy(() => import("./pages/Policy/ReturnPolicy"));
const PrivacyPolicy = React.lazy(() => import("./pages/Policy/PrivacyPolicy"));
const TermsConditions = React.lazy(() => import("./pages/Policy/TermsConditions"));
const CancellationPolicy = React.lazy(() => import("./pages/Policy/CancellationPolicy"));
const Stories = React.lazy(() => import("./pages/Stories/Stories"));
const Cart = React.lazy(() => import("./components/Cart"));
const Checkout = React.lazy(() => import("./components/Checkout"));
const Login = React.lazy(() => import("./components/Login"));
const ForgotPassword = React.lazy(() => import("./components/ForgotPassword"));
const Registration = React.lazy(() => import("./components/Registration"));
const NotFound = React.lazy(() => import("./components/NotFound"));
const NewsletterPopup = React.lazy(() => import("./components/NewsletterPopup"));
const PaymentResponse = React.lazy(() => import("./components/PaymentResponse"));
const PaymentResponseCod = React.lazy(() => import("./components/PaymentResponseCod"));
const CustomPage = React.lazy(() => import("./pages/CustomPage"));
const SearchPage = React.lazy(() => import("./pages/SearchPage"));
const Collaboration = React.lazy(() => import("./pages/Collaboration"));
const CheckoutSuccess = React.lazy(() => import("./pages/CheckoutSuccess"));

function App() {
    const [settings, setSettings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loginData, setLoginData] = useState(null);
    const [cartItems, setCartItems] = useState([]);
    const [grandTotal, setGrandTotal] = useState(null);
    const [wishlistCount, setWishlistCount] = useState(0);
    const [wishlistItem, setWishlistItem] = useState([]);
    const [products, setProducts] = useState([]);
    const [quantities, setQuantities] = useState([]);
    const [cartSummary, setcartSummary] = useState([]);
    const phoneSetting = settings.find(item => item.type === "contact_phone");
    const contactPhone = phoneSetting ? phoneSetting.value : "";
    const [notiData, setNotiData] = useState([]);
    const [notiCount, setNotiCount] = useState(0);
    const headerLogoSetting = settings.find(item => item.type === "header_logo_path");
    const siteIconSetting = settings.find(item => item.type === "site_icon_path");
    const websiteNameSetting = settings.find(item => item.type === "website_name");
    const headerLogoPath = headerLogoSetting ? headerLogoSetting.value : "";
    const siteIconPath = siteIconSetting ? siteIconSetting.value : "";
    const websiteName = websiteNameSetting ? websiteNameSetting.value : "";

    const getGuestId = useCallback(() => {
        let guestId = getCookie("guest_id");
        if (!guestId) {
            guestId = generateGuestId();
            setCookie("guest_id", guestId, 30);
        }
        return guestId;
    }, []);

    const generateGuestId = () => {
        return "guest_" + Math.random().toString(36).substr(2, 9);
    };

    const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(";").shift();
        return null;
    };

    const setCookie = (name, value, days) => {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        const expires = `expires=${date.toUTCString()}`;
        document.cookie = `${name}=${value}; ${expires}; path=/`;
    };

    const fetchCartData = useCallback(async () => {
        let userId = null;
        const token = sessionStorage.getItem("access_token");
        if (loginData && loginData.id) {
            userId = loginData.id;
        } else {
            userId = getGuestId();
        }
        if (userId) {
            const formData = new FormData();
            formData.append(
                loginData && loginData.id ? "user_id" : "temp_user_id",
                userId
            );
            const queryString = new URLSearchParams(formData).toString();
            try {
                const response = await api.post(`/carts?${queryString}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setGrandTotal(response.data.grand_total || 0);
                setCartItems(response.data.data[0]?.cart_items || []);
                setProducts(response.data.data[0]?.cart_items || []);
                setQuantities(
                    response.data.data[0]?.cart_items.map((item) => item.quantity || 1)
                );
                setLoading(false);
            } catch (err) {
                setLoading(false);
            }
        }
    }, [loginData, getGuestId]);

    const fetchNotifData = useCallback(async () => {
        if (!loginData || !loginData.id) {
            return;
        }

        const token = sessionStorage.getItem("access_token");
        if (!token) return;

        try {
            setLoading(true);
            const response = await api.get(`/all-notification`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const notifications = response.data.data || [];
            setNotiData(notifications);

            const unreadCount = notifications.filter(item => !item.isChecked).length;
            setNotiCount(unreadCount);

        } catch (err) {
            // Error handled silently
        } finally {
            setLoading(false);
        }
    }, [loginData]);

    useEffect(() => {
        if (loginData && loginData.id) {
            fetchNotifData();
        }
    }, [loginData, fetchNotifData]);

    useEffect(() => {
        const handleContextMenu = (event) => {
            event.preventDefault();
        };
        document.addEventListener("contextmenu", handleContextMenu);
        return () => {
            document.removeEventListener("contextmenu", handleContextMenu);
        };
    }, []);

    const handleWishlistRemove = useCallback(async (slug) => {
        const token = sessionStorage.getItem("access_token");
        if (!token) {
            toast.info("You need to be logged in to remove items from the wishlist.");
            return;
        }
        try {
            const response = await api.get(`/wishlists-remove-product/${slug}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.data && response.data.is_in_wishlist === false) {
                toast.success(response.data.message);
                setWishlistItem((prevWishlists) =>
                    prevWishlists.filter((item) => item.product.slug !== slug)
                );
                setWishlistCount(wishlistCount - 1);
            } else {
                toast.error("An error occurred while processing your request.");
            }
        } catch (err) {
            toast.error(
                err.response?.data?.message || "An error occurred. Please try again."
            );
        }
    }, [wishlistCount]);

    const fetchCartSummary = useCallback(async () => {
        let userId = null;
        const token = sessionStorage.getItem("access_token");
        if (loginData && loginData.id) {
            userId = loginData.id;
        } else {
            userId = getGuestId();
        }
        if (userId) {
            const formData = new FormData();
            formData.append(
                loginData && loginData.id ? "user_id" : "temp_user_id",
                userId
            );
            const queryString = new URLSearchParams(formData).toString();
            try {
                const response = await api.post(`/cart-summary?${queryString}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                const summary = response.data;
                setcartSummary(summary);
            } catch (err) {
                // Error handled silently, cart summary optional
            } finally {
                setLoading(false);
            }
        }
    }, [loginData, getGuestId]);

    const removeItemFromCart = useCallback(async (cartItemId) => {
        const token = sessionStorage.getItem("access_token");
        const isLoggedIn = !!loginData?.id;
        const userId = isLoggedIn ? loginData.id : getGuestId();

        if (!userId) {
            toast.error("Unable to identify user.");
            return;
        }

        try {
            const response = await api.delete(`/carts/${cartItemId}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });

            if (response.data.result) {
                setProducts((prev) => prev.filter((item) => item.id !== cartItemId));
                setQuantities((prev) => {
                    const updatedProducts = products.filter(item => item.id !== cartItemId);
                    return updatedProducts.map(item => item.quantity || 1);
                });

                await fetchCartData();
                await fetchCartSummary();

                toast.success(response.data.message);
            } else {
                toast.error("An error occurred while processing your request.");
            }
        } catch (error) {
            toast.error(
                error.response?.data?.message || "An error occurred. Please try again."
            );
        }
    }, [loginData, getGuestId, products, fetchCartData, fetchCartSummary]);

    useEffect(() => {
        fetchCartData();
        fetchCartSummary();
    }, [loginData, fetchCartData, fetchCartSummary]);

    const handleQuantityChange = useCallback(async (index, increment) => {
        console.log(index + "   " + increment);
        const newQuantities = [...quantities];
        newQuantities[index] = Math.max(1, newQuantities[index] + increment);
        try {
            const token = sessionStorage.getItem("access_token");
            const response = await api.post(
                "/carts/change-quantity",
                {
                    id: products[index].id,
                    quantity: newQuantities[index],
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const data = response.data;
            if (data.result) {
                console.log("Cart updated successfully:", data.message);
                fetchCartSummary();
                fetchCartData();
            } else {
                alert(data.message);
                setQuantities((prevQuantities) => {
                    const revertedQuantities = [...prevQuantities];
                    revertedQuantities[index] = prevQuantities[index];
                    return revertedQuantities;
                });
            }
        } catch (error) {
            console.error("Error updating cart:", error);
        }
    }, [quantities, products, fetchCartSummary, fetchCartData]);

    const handleCart = useCallback(async (id, variant, user_id, quantity) => {
        let guestId = null;
        let token = sessionStorage.getItem("access_token");

        if (!token) {
            guestId = getCookie("guest_id");

            if (!guestId) {
                toast.info(
                    "You need to be logged in or have a guest session to add items to the cart."
                );
                return;
            }
            token = guestId;
        }
        try {
            const response = await api.post(
                "/carts/add",
                {
                    id,
                    variant,
                    [token === guestId ? "temp_user_id" : "user_id"]:
                        token === guestId ? guestId : user_id,
                    quantity,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data?.result) {
                toast.success(response.data.message);
                fetchCartData(token === guestId ? guestId : user_id);
            } else {
                toast.error(
                    response.data.message ||
                    "An error occurred while processing your request."
                );
            }
        } catch (err) {
            toast.error(
                err.response?.data?.message || "An error occurred. Please try again."
            );
        }
    }, [fetchCartData]);

    const fetchWishlistCount = useCallback(async () => {
        const token = sessionStorage.getItem("access_token");
        if (!token) {
            setWishlistCount(0);
            return;
        }
        try {
            const response = await api.get("/wishlists", {
                headers: { Authorization: `Bearer ${token}` },
            });
            setWishlistItem(response.data.data);
            setWishlistCount(response.data.data.length);
        } catch {
            setWishlistCount(0);
        }
    }, []);

    const handleWishlist = useCallback(async (slug) => {
        const token = sessionStorage.getItem("access_token");
        if (!token) {
            toast.info("You need to be logged in to add items to the wishlist.");
            return;
        }
        try {
            const response = await api.get(`/wishlists-add-product/${slug}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data?.result) {
                fetchWishlistCount();
                toast.success(response.data.message);
            } else {
                toast.error("An error occurred while processing your request.");
            }
        } catch (err) {
            toast.error(
                err.response?.data?.message || "An error occurred. Please try again."
            );
        }
    }, [fetchWishlistCount]);

    useEffect(() => {
        fetchWishlistCount();
    }, [fetchWishlistCount]);

    useEffect(() => {
        const userData = sessionStorage.getItem("user_data");
        if (userData) {
            try {
                const parsedData = JSON.parse(userData);
                setLoginData(parsedData);
            } catch {
                setLoginData(null);
            }
        } else {
            setLoginData(null);
        }
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await api.get("/business-settings");
                setSettings(response.data.data.data);
            } catch (err) {
                // Error handled silently
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (!siteIconPath) return;

        const updateFavicon = (rel) => {
            let link = document.querySelector(`link[rel='${rel}']`);
            if (!link) {
                link = document.createElement("link");
                link.rel = rel;
                document.head.appendChild(link);
            }
            link.href = siteIconPath;
        };

        updateFavicon("icon");
        updateFavicon("shortcut icon");
        window.__SITE_ICON__ = siteIconPath;
    }, [siteIconPath]);

    useEffect(() => {
        if (headerLogoPath) {
            window.__SITE_LOGO__ = headerLogoPath;
        }
        if (websiteName) {
            window.__SITE_NAME__ = websiteName;
        }
    }, [headerLogoPath, websiteName]);


    // Loading fallback component
    const SuspenseFallback = () => (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <Preloader logoUrl={siteIconPath || "/images/favicon.png"} siteName={websiteName} />
        </div>
    );

    return (
        <ToastProvider>
            <Router>
                <ModernToastContainer />
                <ScrollToTop />
                <Suspense fallback={null}>
                    <NewsletterPopup />
                </Suspense>
                <div className="App">
                {loading && <Preloader logoUrl={siteIconPath || "/images/favicon.png"} siteName={websiteName} />}
                <Header cartItems={cartItems} grandTotal={grandTotal} removeItemFromCart={removeItemFromCart} wishlistCount={wishlistCount} notiCount={notiCount} notiData={notiData} />
                <LocationToast />
                <Suspense fallback={<SuspenseFallback />}>
                <Routes>
                    <Route path="/" element={
                        <Home handleCart={handleCart} handleWishlist={handleWishlist} wishlistItem={wishlistItem} />
                    }
                    />
                    <Route path="/search" element={<SearchPage handleCart={handleCart} handleWishlist={handleWishlist} />} />
                    <Route
                        path="/products"
                        element={<Products handleQuantityChange={handleQuantityChange} />}
                    />
                    <Route
                        path="/products/:slug"
                        element={
                            <ProductDetails
                                handleQuantityChange={handleQuantityChange}
                                handleCart={handleCart}
                                handleWishlist={handleWishlist}
                            />
                        }
                    />
                    <Route
                        path="/category/:slug"
                        element={
                            <ProductCategories
                                handleCart={handleCart}
                                handleWishlist={handleWishlist}
                            />
                        }
                    />
                    <Route
                        path="/category/:slug/:slug"
                        element={
                            <ProductSubCategories
                                handleCart={handleCart}
                                handleWishlist={handleWishlist}
                            />
                        }
                    />
                    <Route path="/about-us" element={<AboutUs setting={settings} />} />
                    <Route
                        path="/contact-us"
                        element={<ContactUs setting={settings} />}
                    />
                    <Route path="/why-us" element={<WhyUs setting={settings} />} />
                    <Route path="/career" element={<Career setting={settings} />} />
                    <Route path="/sitemap" element={<Sitemap setting={settings} />} />
                    <Route path="/faqs" element={<Faqs setting={settings} />} />
                    <Route path="/blogs" element={<Blogs />} />
                    <Route path="/blog/:slug" element={<BlogDetails />} />
                    <Route path="/projects" element={<Projects />} />
                    <Route path="/project/:slug" element={<ProjectDetails />} />
                    <Route path="/stories" element={<Stories />} />
                    <Route
                        path="/cart"
                        element={
                            <Cart
                                products={cartItems}
                                quantities={quantities}
                                handleQuantityChange={handleQuantityChange}
                                removeItemFromCart={removeItemFromCart}
                                cartSummary={cartSummary}
                            />
                        }
                    />

                    <Route path="/checkout" element={<Checkout cartSummary={cartSummary} refreshCartSummary={fetchCartSummary} />} />


                    <Route path="/payment-response" element={<PaymentResponse />} />
                    <Route path="/payment-cod-response" element={<PaymentResponseCod />} />
                    <Route path="/checkout-success" element={<CheckoutSuccess />} />
                    <Route path="/registration" element={<Registration />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route
                        path="/dashboard"
                        element={
                            loginData ? <Dashboard /> : <Navigate to="/login" replace />
                        }
                    />
                    <Route path="/purchase-history" element={<PurchaseHistory />} />
                    <Route path="/notifications" element={<Notification notiCount={notiCount} notiData={notiData} />} />
                    <Route path="/order-details/:id" element={<OrderDetails />} />
                    <Route
                        path="/wishlists"
                        element={
                            <Wishlists
                                handleCart={handleCart}
                                wishlistItem={wishlistItem}
                                handleWishlistRemove={handleWishlistRemove}
                            />
                        }
                    />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/terms-conditions" element={<TermsConditions setting={settings} />} />
                    <Route path="/warranty-policy" element={<WarrantyPolicy setting={settings} />} />
                    <Route path="/shipping-policy" element={<ShippingPolicy setting={settings} />} />
                    <Route path="/return-policy" element={<ReturnPolicy setting={settings} />} />
                    <Route path="/return-refund" element={<Navigate to="/return-policy" replace />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy setting={settings} />} />
                    <Route path="/cancellation-policy" element={<CancellationPolicy setting={settings} />} />
                    <Route path="/collaboration" element={<Collaboration setting={settings} />} />
                    <Route path="/:slug" element={<CustomPage />} />
                    <Route path="*" element={<NotFound />} />
                </Routes>
                </Suspense>
                <Footer setting={settings} />
                <ScrollButton />
                <a
                    href={`https://wa.me/${contactPhone}`}
                    className="whatsapp-float"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <img
                        src="https://cdn-icons-png.flaticon.com/512/733/733585.png"
                        alt="WhatsApp"
                        loading="lazy"
                        style={{ width: "35px", height: "35px" }}
                    />
                </a>
            </div>
        </Router>
        </ToastProvider>
    );
}
const LocationToast = memo(() => {
    const location = useLocation();

    useEffect(() => {
        if (location.state?.message) {
            toast.success(location.state.message);
        }
        if (location.state?.error) {
            toast.error(location.state.error);
        }
    }, [location.state]);

    return null;
});
LocationToast.displayName = 'LocationToast';

export default App;
